import { createHistoryChecksum } from '../HistoryChecksum';
import type {
    HistoryBlobDescriptor,
    HistoryBlobWriteRequest,
    HistoryCheckpointRecord,
    HistoryDocumentStorageMeta,
    HistoryPersistence,
    HistoryPersistenceTransactionRecord,
    MarkHistoryTransactionCommittedInput,
    MarkHistoryTransactionsDiscardedInput,
    UpdateHistoryDocumentCursorInput,
} from '../HistoryPersistenceTypes';

/**
 * IndexedDB 历史持久化配置。
 */
export interface CreateIndexedDBHistoryPersistenceOptions {
    /** 数据库名称。 */
    readonly databaseName?: string;
    /** 数据库版本。 */
    readonly databaseVersion?: number;
    /** 可选外部 blob 写入器；通常由 OPFS adapter 注入。 */
    readonly writeBlob?: NonNullable<HistoryPersistence['writeBlob']>;
}

const HISTORY_DOCUMENTS_STORE = 'historyDocuments';
const HISTORY_TRANSACTIONS_STORE = 'historyTransactions';
const HISTORY_CHECKPOINTS_STORE = 'historyCheckpoints';
const HISTORY_BLOBS_STORE = 'historyBlobs';
const HISTORY_SCHEMA_MIGRATIONS_STORE = 'historySchemaMigrations';

/**
 * IndexedDB 事务模式。
 */
type IndexedDBTransactionMode = IDBTransactionMode;

/**
 * 将 IDBRequest 转成 Promise。
 *
 * @param request IndexedDB 请求。
 * @returns 请求结果。
 */
function requestToPromise<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB 请求失败。'));
    });
}

/**
 * 等待 IndexedDB 事务完成。
 *
 * @param transaction IndexedDB 事务。
 */
function waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB 事务失败。'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB 事务已中止。'));
    });
}

/**
 * 拷贝 Uint8Array 为独立 ArrayBuffer。
 *
 * @param bytes 字节数组。
 * @returns ArrayBuffer。
 */
function copyBytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    const buffer = new ArrayBuffer(bytes.byteLength);

    new Uint8Array(buffer).set(bytes);

    return buffer;
}

/**
 * 将 blob 内容转换为 ArrayBuffer。
 *
 * @param content blob 内容。
 * @returns ArrayBuffer。
 */
function toArrayBuffer(content: HistoryBlobWriteRequest['content']): ArrayBuffer {
    if (typeof content === 'string') {
        return copyBytesToArrayBuffer(new TextEncoder().encode(content));
    }

    if (content instanceof Uint8Array) {
        return copyBytesToArrayBuffer(content);
    }

    return content.slice(0);
}

/**
 * 打开历史 IndexedDB。
 *
 * @param options IndexedDB 历史持久化配置。
 * @returns 数据库连接。
 */
function openHistoryDatabase(options: CreateIndexedDBHistoryPersistenceOptions): Promise<IDBDatabase> {
    const databaseName = options.databaseName ?? 'PuppyCADHistory';
    const databaseVersion = options.databaseVersion ?? 1;

    return new Promise((resolve, reject) => {
        const request = indexedDB.open(databaseName, databaseVersion);

        request.onupgradeneeded = () => {
            const database = request.result;

            if (!database.objectStoreNames.contains(HISTORY_DOCUMENTS_STORE)) {
                database.createObjectStore(HISTORY_DOCUMENTS_STORE, { keyPath: 'documentId' });
            }

            if (!database.objectStoreNames.contains(HISTORY_TRANSACTIONS_STORE)) {
                const store = database.createObjectStore(HISTORY_TRANSACTIONS_STORE, {
                    keyPath: ['documentId', 'transactionId'],
                });

                store.createIndex('byDocumentStatus', ['documentId', 'status'], { unique: false });
                store.createIndex('byDocumentSeq', ['documentId', 'seq'], { unique: false });
            }

            if (!database.objectStoreNames.contains(HISTORY_CHECKPOINTS_STORE)) {
                const store = database.createObjectStore(HISTORY_CHECKPOINTS_STORE, {
                    keyPath: ['documentId', 'checkpointId'],
                });

                store.createIndex('byDocumentSeq', ['documentId', 'seq'], { unique: false });
            }

            if (!database.objectStoreNames.contains(HISTORY_BLOBS_STORE)) {
                database.createObjectStore(HISTORY_BLOBS_STORE, { keyPath: 'blobId' });
            }

            if (!database.objectStoreNames.contains(HISTORY_SCHEMA_MIGRATIONS_STORE)) {
                database.createObjectStore(HISTORY_SCHEMA_MIGRATIONS_STORE, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('打开历史 IndexedDB 失败。'));
    });
}

/**
 * 在指定 store 上运行 IndexedDB 操作。
 *
 * @param options IndexedDB 历史持久化配置。
 * @param storeNames store 名称。
 * @param mode 事务模式。
 * @param work 事务操作。
 * @returns 操作结果。
 */
async function runIndexedDBTransaction<TResult>(
    options: CreateIndexedDBHistoryPersistenceOptions,
    storeNames: readonly string[],
    mode: IndexedDBTransactionMode,
    work: (transaction: IDBTransaction) => Promise<TResult> | TResult,
): Promise<TResult> {
    const database = await openHistoryDatabase(options);

    try {
        const transaction = database.transaction([...storeNames], mode);
        const result = await work(transaction);

        await waitForTransaction(transaction);

        return result;
    } finally {
        database.close();
    }
}

/**
 * 写入 IndexedDB 小型历史 blob。
 *
 * @param options IndexedDB 历史持久化配置。
 * @param request blob 写入请求。
 * @returns blob 描述。
 */
async function writeIndexedDBHistoryBlob(
    options: CreateIndexedDBHistoryPersistenceOptions,
    request: HistoryBlobWriteRequest,
): Promise<HistoryBlobDescriptor> {
    const content = toArrayBuffer(request.content);
    const contentBytes = Array.from(new Uint8Array(content));
    const descriptor: HistoryBlobDescriptor = {
        blobId: request.blobId,
        hash: createHistoryChecksum(contentBytes),
        byteLength: content.byteLength,
        storageTier: 'indexeddb',
        storageKey: request.blobId,
        mediaType: request.mediaType,
    };

    await runIndexedDBTransaction(options, [HISTORY_BLOBS_STORE], 'readwrite', async (transaction) => {
        transaction.objectStore(HISTORY_BLOBS_STORE).put({
            ...descriptor,
            content,
            createdAt: Date.now(),
        });
    });

    return descriptor;
}

/**
 * 读取索引中的匹配记录。
 *
 * @param index IndexedDB 索引。
 * @param range 查询范围。
 * @returns 记录数组。
 */
async function getAllFromIndex<TRecord>(index: IDBIndex, range: IDBKeyRange): Promise<TRecord[]> {
    return await requestToPromise(index.getAll(range)) as TRecord[];
}

/**
 * 创建指定文档的事务序号查询范围。
 *
 * @param documentId 文档 ID。
 * @param afterSeq 起始事务序号，结果不包含该序号。
 * @param untilSeq 结束事务序号，结果包含该序号。
 * @returns IndexedDB 组合索引范围。
 */
function createDocumentSeqRange(
    documentId: string,
    afterSeq?: number,
    untilSeq?: number,
): IDBKeyRange {
    const lowerSeq = afterSeq ?? Number.NEGATIVE_INFINITY;
    const upperSeq = untilSeq ?? Number.POSITIVE_INFINITY;

    return IDBKeyRange.bound(
        [documentId, lowerSeq],
        [documentId, upperSeq],
        afterSeq !== undefined,
        false,
    );
}

/**
 * 创建浏览器 IndexedDB 历史持久化端口。
 *
 * @param options IndexedDB 历史持久化配置。
 * @returns 历史持久化端口。
 */
export function createIndexedDBHistoryPersistence(
    options: CreateIndexedDBHistoryPersistenceOptions = {},
): HistoryPersistence {
    const writeBlob = options.writeBlob
        ?? ((request: HistoryBlobWriteRequest) => writeIndexedDBHistoryBlob(options, request));

    return {
        async writePendingTransaction(record): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readwrite', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);
                const key = [record.documentId, record.transactionId];
                const existingRecord = await requestToPromise(store.get(key)) as HistoryPersistenceTransactionRecord | undefined;

                if (existingRecord?.status === 'committed') {
                    throw new Error(`历史事务已提交，不能重新写入 pending：${record.documentId}/${record.transactionId}`);
                }

                if (existingRecord?.status === 'discarded') {
                    throw new Error(`历史事务已丢弃，不能重新写入 pending：${record.documentId}/${record.transactionId}`);
                }

                store.put(record);
            });
        },
        writeBlob,
        async writeCheckpoint(record): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_CHECKPOINTS_STORE], 'readwrite', async (transaction) => {
                transaction.objectStore(HISTORY_CHECKPOINTS_STORE).put(record);
            });
        },
        async markTransactionCommitted(input: MarkHistoryTransactionCommittedInput): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readwrite', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);
                const key = [input.documentId, input.transactionId];
                const record = await requestToPromise(store.get(key)) as HistoryPersistenceTransactionRecord | undefined;

                if (!record) {
                    throw new Error(`历史事务缺少 pending journal，不能标记为 committed：${input.documentId}/${input.transactionId}`);
                }

                store.put({
                    ...record,
                    status: 'committed',
                    checksum: input.checksum,
                    blobRefs: input.blobRefs,
                    checkpointId: input.checkpointId,
                    updatedAt: input.updatedAt,
                });
            });
        },
        async markTransactionDiscarded(documentId, transactionId, updatedAt): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readwrite', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);
                const key = [documentId, transactionId];
                const record = await requestToPromise(store.get(key)) as HistoryPersistenceTransactionRecord | undefined;

                if (!record) {
                    return;
                }

                store.put({
                    ...record,
                    status: 'discarded',
                    updatedAt,
                });
            });
        },
        async markTransactionsDiscarded(input: MarkHistoryTransactionsDiscardedInput): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readwrite', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);

                for (const transactionId of input.transactionIds) {
                    const key = [input.documentId, transactionId];
                    const record = await requestToPromise(store.get(key)) as HistoryPersistenceTransactionRecord | undefined;

                    if (!record) {
                        continue;
                    }

                    store.put({
                        ...record,
                        status: 'discarded',
                        updatedAt: input.updatedAt,
                    });
                }
            });
        },
        async updateDocumentCursor(input: UpdateHistoryDocumentCursorInput): Promise<void> {
            await runIndexedDBTransaction(options, [HISTORY_DOCUMENTS_STORE], 'readwrite', async (transaction) => {
                transaction.objectStore(HISTORY_DOCUMENTS_STORE).put({
                    documentId: input.documentId,
                    currentSeq: input.currentSeq,
                    undoCursorSeq: input.undoCursorSeq,
                    latestCheckpointSeq: input.latestCheckpointSeq,
                    updatedAt: input.updatedAt,
                });
            });
        },
        async loadDocumentMeta(documentId): Promise<HistoryDocumentStorageMeta | null> {
            return await runIndexedDBTransaction(options, [HISTORY_DOCUMENTS_STORE], 'readonly', async (transaction) => {
                return await requestToPromise(transaction.objectStore(HISTORY_DOCUMENTS_STORE).get(documentId)) ?? null;
            });
        },
        async loadCommittedTransactions(input): Promise<readonly HistoryPersistenceTransactionRecord[]> {
            return await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readonly', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);
                const records = await getAllFromIndex<HistoryPersistenceTransactionRecord>(
                    store.index('byDocumentSeq'),
                    createDocumentSeqRange(input.documentId, input.afterSeq, input.untilSeq),
                );

                return records
                    .filter((record) => record.status === 'committed')
                    .sort((left, right) => left.seq - right.seq);
            });
        },
        async loadPendingTransactions(documentId): Promise<readonly HistoryPersistenceTransactionRecord[]> {
            return await runIndexedDBTransaction(options, [HISTORY_TRANSACTIONS_STORE], 'readonly', async (transaction) => {
                const store = transaction.objectStore(HISTORY_TRANSACTIONS_STORE);
                const records = await getAllFromIndex<HistoryPersistenceTransactionRecord>(
                    store.index('byDocumentStatus'),
                    IDBKeyRange.only([documentId, 'pending']),
                );

                return records.sort((left, right) => left.seq - right.seq);
            });
        },
    };
}

/**
 * 清空浏览器 IndexedDB 中的会话历史数据。
 *
 * @param options IndexedDB 历史持久化配置。
 */
export async function clearIndexedDBHistoryPersistence(
    options: CreateIndexedDBHistoryPersistenceOptions = {},
): Promise<void> {
    await runIndexedDBTransaction(
        options,
        [
            HISTORY_DOCUMENTS_STORE,
            HISTORY_TRANSACTIONS_STORE,
            HISTORY_CHECKPOINTS_STORE,
            HISTORY_BLOBS_STORE,
            HISTORY_SCHEMA_MIGRATIONS_STORE,
        ],
        'readwrite',
        async (transaction) => {
            transaction.objectStore(HISTORY_DOCUMENTS_STORE).clear();
            transaction.objectStore(HISTORY_TRANSACTIONS_STORE).clear();
            transaction.objectStore(HISTORY_CHECKPOINTS_STORE).clear();
            transaction.objectStore(HISTORY_BLOBS_STORE).clear();
            transaction.objectStore(HISTORY_SCHEMA_MIGRATIONS_STORE).clear();
        },
    );
}
